import { useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMergeLeads } from "../hooks/useLeads";
import { LeadStatusBadge } from "./LeadStatusBadge";
import type { LeadDetail, MergeLeadsPayload } from "../types";

/**
 * Side-by-side merge picker. For each comparable field the user toggles
 * "Keep A" (target, current) vs "Keep B" (source, will be merged-away).
 * On submit, only fields where the user chose B are sent as overrides;
 * everything else inherits the target's existing value.
 */
type FieldKey =
  | "name"
  | "email"
  | "phone"
  | "companyName"
  | "jobTitle"
  | "country"
  | "status"
  | "ownerId"
  | "notes"
  | "linkedContactId"
  | "linkedCompanyId"
  | "linkedProjectId";

const FIELD_LABELS: Record<FieldKey, string> = {
  name: "Name",
  email: "Email",
  phone: "Phone",
  companyName: "Company",
  jobTitle: "Job Title",
  country: "Country",
  status: "Status",
  ownerId: "Owner",
  notes: "Notes",
  linkedContactId: "Linked Contact",
  linkedCompanyId: "Linked Company",
  linkedProjectId: "Linked Project",
};

type FieldValueResolver = (l: LeadDetail) => string | null;

const FIELD_RESOLVERS: Record<FieldKey, FieldValueResolver> = {
  name: (l) => l.name,
  email: (l) => l.email,
  phone: (l) => l.phone,
  companyName: (l) => l.companyName,
  jobTitle: (l) => l.jobTitle,
  country: (l) => l.country,
  status: (l) => l.status,
  ownerId: (l) => l.ownerName ?? l.ownerId,
  notes: (l) => l.notes,
  linkedContactId: (l) => l.linkedContactName ?? l.linkedContactId,
  linkedCompanyId: (l) => l.linkedCompanyName ?? l.linkedCompanyId,
  linkedProjectId: (l) => l.linkedProjectName ?? l.linkedProjectId,
};

const FIELD_RAW_RESOLVERS: Record<FieldKey, FieldValueResolver> = {
  name: (l) => l.name,
  email: (l) => l.email,
  phone: (l) => l.phone,
  companyName: (l) => l.companyName,
  jobTitle: (l) => l.jobTitle,
  country: (l) => l.country,
  status: (l) => l.status,
  ownerId: (l) => l.ownerId,
  notes: (l) => l.notes,
  linkedContactId: (l) => l.linkedContactId,
  linkedCompanyId: (l) => l.linkedCompanyId,
  linkedProjectId: (l) => l.linkedProjectId,
};

const FIELDS: FieldKey[] = [
  "name",
  "email",
  "phone",
  "companyName",
  "jobTitle",
  "country",
  "status",
  "ownerId",
  "notes",
  "linkedContactId",
  "linkedCompanyId",
  "linkedProjectId",
];

export function MergeLeadsModal({
  target,
  source,
  open,
  onOpenChange,
}: {
  target: LeadDetail;
  source: LeadDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  // Per-field selection: "A" keeps target's value (default), "B" picks source's.
  const [choices, setChoices] = useState<Record<FieldKey, "A" | "B">>(() =>
    FIELDS.reduce(
      (acc, k) => {
        // Sensible default: prefer A unless A is blank and B has a value.
        const a = FIELD_RAW_RESOLVERS[k](target);
        const b = FIELD_RAW_RESOLVERS[k](source);
        acc[k] = !a && b ? "B" : "A";
        return acc;
      },
      {} as Record<FieldKey, "A" | "B">,
    ),
  );

  const [confirming, setConfirming] = useState(false);
  const merge = useMergeLeads(target.id);
  const [error, setError] = useState<string | null>(null);

  // Build the overrides payload: only include fields the user chose B for.
  const fieldOverrides: MergeLeadsPayload["fieldOverrides"] = useMemo(() => {
    const o: MergeLeadsPayload["fieldOverrides"] = {};
    for (const k of FIELDS) {
      if (choices[k] !== "B") continue;
      const v = FIELD_RAW_RESOLVERS[k](source);
      // Type system: each field has its own shape; cast as any here is
      // intentional — the schema validates on the backend.
      (o as Record<string, unknown>)[k] = v;
    }
    return o;
  }, [choices, source]);

  async function handleMerge() {
    setError(null);
    try {
      await merge.mutateAsync({
        sourceLeadId: source.id,
        fieldOverrides,
      });
      onOpenChange(false);
    } catch (e) {
      setError((e as Error)?.message ?? "Merge failed");
      setConfirming(false);
    }
  }

  function displayValue(v: string | null): React.ReactNode {
    if (!v) return <span className="text-muted-foreground">—</span>;
    return v;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Merge {source.code} into {target.code}</DialogTitle>
          <DialogDescription>
            For each field, pick which value to keep. The source Lead will be
            soft-deleted; its enquiries, notes, and tasks move to the target.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Field</th>
                <th className="px-3 py-2 text-left">
                  <div className="flex items-center gap-1">
                    A <span className="text-[10px]">(target — keep)</span>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {target.code}
                  </div>
                </th>
                <th className="px-3 py-2 text-left">
                  <div className="flex items-center gap-1">
                    B <span className="text-[10px]">(source — merging in)</span>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {source.code}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {FIELDS.map((k) => {
                const aDisplay = FIELD_RESOLVERS[k](target);
                const bDisplay = FIELD_RESOLVERS[k](source);
                const aRaw = FIELD_RAW_RESOLVERS[k](target);
                const bRaw = FIELD_RAW_RESOLVERS[k](source);
                const same = aRaw === bRaw;
                return (
                  <tr key={k}>
                    <td className="px-3 py-2 font-medium text-foreground">
                      {FIELD_LABELS[k]}
                    </td>
                    <td className="px-3 py-2">
                      <label className="flex cursor-pointer items-start gap-2">
                        <input
                          type="radio"
                          checked={choices[k] === "A"}
                          onChange={() =>
                            setChoices((p) => ({ ...p, [k]: "A" }))
                          }
                          disabled={same}
                          className="mt-1"
                        />
                        <span
                          className={
                            choices[k] === "A"
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }
                        >
                          {k === "status" && aRaw ? (
                            <LeadStatusBadge status={aRaw} />
                          ) : (
                            displayValue(aDisplay)
                          )}
                        </span>
                      </label>
                    </td>
                    <td className="px-3 py-2">
                      <label className="flex cursor-pointer items-start gap-2">
                        <input
                          type="radio"
                          checked={choices[k] === "B"}
                          onChange={() =>
                            setChoices((p) => ({ ...p, [k]: "B" }))
                          }
                          disabled={same}
                          className="mt-1"
                        />
                        <span
                          className={
                            choices[k] === "B"
                              ? "text-foreground"
                              : "text-muted-foreground"
                          }
                        >
                          {k === "status" && bRaw ? (
                            <LeadStatusBadge status={bRaw} />
                          ) : (
                            displayValue(bDisplay)
                          )}
                        </span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div
          className="rounded-md border bg-[hsl(38,92%,97%)] p-3 text-xs"
          style={{ borderColor: "hsl(38,92%,80%)" }}
        >
          <div className="flex items-start gap-2 text-[hsl(32,80%,34%)]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
            <div>
              This will move {source.enquiryCount} enquir
              {source.enquiryCount === 1 ? "y" : "ies"} from {source.code} to{" "}
              {target.code}. {source.code} will be soft-deleted with a merge
              reference. The new total on {target.code} becomes{" "}
              {target.enquiryCount + source.enquiryCount}.
            </div>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          {!confirming ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={merge.isPending}
              >
                Cancel
              </Button>
              <Button onClick={() => setConfirming(true)} disabled={merge.isPending}>
                Continue
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setConfirming(false)}
                disabled={merge.isPending}
              >
                Back
              </Button>
              <Button onClick={handleMerge} disabled={merge.isPending}>
                {merge.isPending && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
                )}
                Confirm merge
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
